import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import slugify from '@/constants/utils/slugify';
import { chatsApi } from '@/lib/api';

function formatDate(timestamp) {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function getRoleName(message, getModelById) {
  if (message.role === 'user') return 'You';
  if (message.role === 'assistant') {
    if (Array.isArray(message.modelIds) && message.modelIds.length > 0 && getModelById) {
      const model = getModelById(message.modelIds[0]);
      if (model?.name) return model.name;
    }
    return 'Assistant';
  }
  return message.role || 'Unknown';
}

function getVisibleMessages(messages) {
  if (!Array.isArray(messages)) return [];
  return messages.filter(
    (m) => m.role === 'user' || m.role === 'assistant'
  ).filter(
    (m) => !m.flags?.isDeleted && !m.flags?.isHidden
  ).filter(
    (m) => m.content || (Array.isArray(m.images) && m.images.length > 0)
  );
}

function getImagePlaceholder(images) {
  if (!Array.isArray(images) || images.length === 0) return '';
  const count = images.length;
  const lines = [];
  for (let i = 0; i < count; i++) {
    const img = images[i];
    const prompt = img?.prompt;
    if (prompt) {
      lines.push(`[Image ${count > 1 ? `${i + 1}/${count}` : ''}: ${prompt}]`.replace(' :', ':'));
    } else {
      lines.push(`[Image${count > 1 ? ` ${i + 1}/${count}` : ''}]`);
    }
  }
  return lines.join('\n');
}

/**
 * Convert a conversation to Markdown
 */
export function toMarkdown(chat, { getModelById } = {}) {
  const title = chat.title || 'Untitled Conversation';
  const createdAt = formatDate(chat.createdAt);
  const messages = getVisibleMessages(chat.messages);

  const lines = [];
  lines.push(`# ${title}`);
  if (createdAt) lines.push(`*${createdAt}*`);
  lines.push('');
  lines.push('---');
  lines.push('');

  for (const msg of messages) {
    const role = getRoleName(msg, getModelById);
    const time = formatDate(msg.timestamp);
    lines.push(`### ${role}`);
    if (time) lines.push(`*${time}*`);
    lines.push('');

    const imagePlaceholder = getImagePlaceholder(msg.images);
    if (imagePlaceholder) {
      lines.push(imagePlaceholder);
      lines.push('');
    }

    if (msg.content) {
      lines.push(msg.content);
      lines.push('');
    }

    lines.push('---');
    lines.push('');
  }

  lines.push(`*Exported from Gab AI on ${formatDate(new Date())}*`);

  return lines.join('\n');
}

/**
 * Convert a conversation to plain text
 */
export function toPlainText(chat, { getModelById } = {}) {
  const title = chat.title || 'Untitled Conversation';
  const createdAt = formatDate(chat.createdAt);
  const messages = getVisibleMessages(chat.messages);

  const lines = [];
  lines.push(title);
  if (createdAt) lines.push(createdAt);
  lines.push('='.repeat(Math.min(title.length, 60)));
  lines.push('');

  for (const msg of messages) {
    const role = getRoleName(msg, getModelById);
    const time = formatDate(msg.timestamp);
    lines.push(`[${role}]${time ? ` (${time})` : ''}`);

    const imagePlaceholder = getImagePlaceholder(msg.images);
    if (imagePlaceholder) {
      lines.push(imagePlaceholder);
    }

    if (msg.content) {
      lines.push(msg.content);
    }

    lines.push('');
    lines.push('-'.repeat(40));
    lines.push('');
  }

  lines.push(`Exported from Gab AI on ${formatDate(new Date())}`);

  return lines.join('\n');
}

/**
 * Trigger a file download on web via blob (string content)
 */
function downloadBlobWeb(content, filename, mimeType) {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Share a text file on native via the share sheet
 */
async function shareFileNative(content, filename, mimeType) {
  const fileUri = `${FileSystem.cacheDirectory}${filename}`;
  await FileSystem.writeAsStringAsync(fileUri, content, {
    encoding: FileSystem.EncodingType.UTF8,
  });

  const isAvailable = await Sharing.isAvailableAsync();
  if (!isAvailable) {
    throw new Error('Sharing is not available on this device');
  }

  await Sharing.shareAsync(fileUri, {
    mimeType,
    dialogTitle: 'Export Conversation',
  });

  setTimeout(async () => {
    try {
      await FileSystem.deleteAsync(fileUri, { idempotent: true });
    } catch (_) {
      // ignore cleanup
    }
  }, 60000);
}

/**
 * Convert a Blob to a base64 string. Used to write a binary file (e.g. a
 * PDF) to the native cache directory before handing it to the share sheet.
 */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Failed to read blob'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        const commaIdx = result.indexOf(',');
        resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
      } else {
        reject(new Error('Unexpected reader result type'));
      }
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Share a binary blob (e.g. PDF bytes) on native via the share sheet.
 */
async function shareBinaryNative(blob, filename, mimeType) {
  const base64 = await blobToBase64(blob);
  const fileUri = `${FileSystem.cacheDirectory}${filename}`;
  await FileSystem.writeAsStringAsync(fileUri, base64, {
    encoding: FileSystem.EncodingType.Base64,
  });

  const isAvailable = await Sharing.isAvailableAsync();
  if (!isAvailable) {
    throw new Error('Sharing is not available on this device');
  }

  await Sharing.shareAsync(fileUri, {
    mimeType,
    dialogTitle: 'Export Conversation',
  });

  setTimeout(async () => {
    try {
      await FileSystem.deleteAsync(fileUri, { idempotent: true });
    } catch (_) {
      // ignore cleanup
    }
  }, 60000);
}

function exportExtension(format) {
  return format === 'pdf' ? 'pdf'
    : format === 'text' ? 'txt'
    : 'md';
}

/**
 * Pick a download filename. Prefers the server's Content-Disposition
 * (which keeps Unicode titles). slugify() strips every non-ASCII character,
 * so a CJK/emoji-only title used to become ".pdf" and iOS sharing would
 * refuse the file — the same chats the Unicode PDF renderer was meant to
 * fix.
 */
export function conversationExportFilename(chat, format, serverFilename) {
  const ext = exportExtension(format);
  if (serverFilename) {
    const base = String(serverFilename).split(/[/\\]/).pop() || '';
    const cleaned = base.replace(/[\0\r\n]/g, '').trim();
    if (cleaned && cleaned !== `.${ext}`) return cleaned;
  }
  const slug = slugify(chat?.title || 'conversation') || 'conversation';
  return `${slug}.${ext}`;
}

function blobFromExportResult(blobOrResult) {
  if (blobOrResult && typeof blobOrResult === 'object' && blobOrResult.blob) {
    return blobOrResult.blob;
  }
  return blobOrResult;
}

/**
 * Save a binary Blob produced by the server-side export (e.g. a rendered
 * PDF, or any format when the client falls back to the server). On web
 * this triggers a browser download; on native it writes the blob to the
 * cache directory and hands it to the OS share sheet.
 *
 * Kept separate from `exportConversation` so callers that already have a
 * blob (the no-local-messages fallback path, the PDF branch) can reuse it
 * without re-fetching.
 *
 * `blobOrResult` may be a Blob or `{ blob, filename }` from fetchWithAuth
 * with `responseType: 'blob'`.
 */
export async function saveExportedBlob(blobOrResult, chat, format) {
  const blob = blobFromExportResult(blobOrResult);
  const serverFilename = typeof blobOrResult?.filename === 'string' ? blobOrResult.filename : null;
  const filename = conversationExportFilename(chat, format, serverFilename);
  const mimeType = format === 'pdf' ? 'application/pdf'
    : format === 'text' ? 'text/plain'
    : 'text/markdown';

  if (Platform.OS === 'web') {
    downloadBlobWeb(blob, filename, mimeType);
  } else {
    await shareBinaryNative(blob, filename, mimeType);
  }
}

/**
 * Export a full conversation in the given format.
 *
 * Markdown / text exports are built CLIENT-SIDE from the in-memory chat
 * object (so the export reflects exactly what the user sees, including
 * any local edits / pending state). PDF exports are built SERVER-SIDE so
 * the heavy markdown -> PDF rendering doesn't block the UI thread.
 *
 * On web: triggers a browser download.
 * On native: opens the OS share sheet.
 */
export async function exportConversation(chat, { format = 'markdown', getModelById } = {}) {
  const opts = { getModelById };
  const baseFilename = slugify(chat.title || 'conversation') || 'conversation';

  // PDF -> server-side render
  if (format === 'pdf') {
    if (!chat?.id) {
      throw new Error('Cannot export PDF: conversation id missing');
    }
    const result = await chatsApi.downloadConversationExport(chat.id, 'pdf');
    await saveExportedBlob(result, chat, 'pdf');
    return;
  }

  // Markdown / text -> client-side build
  let content, filename, mimeType;
  if (format === 'text') {
    content = toPlainText(chat, opts);
    filename = `${baseFilename}.txt`;
    mimeType = 'text/plain';
  } else {
    content = toMarkdown(chat, opts);
    filename = `${baseFilename}.md`;
    mimeType = 'text/markdown';
  }

  if (Platform.OS === 'web') {
    downloadBlobWeb(content, filename, mimeType);
  } else {
    await shareFileNative(content, filename, mimeType);
  }
}

export default exportConversation;
